import { appNoOp } from '@actions/app.action';
import { bookingTripDetailsInit } from '@actions/booking.action';
import {
  plusgradeGetEligibility,
  plusgradeGetEligibilitySuccess,
} from '@actions/plusgrade.action';
import { Injectable } from '@angular/core';
import { ConfirmationMessageModalComponent } from '@components/modals/confirmation-message-modal/confirmation-message-modal.component';
import { MODAL_DIALOG_CENTER } from '@constants/app.constants';
import {
  PlusgradeCallOrigin,
  PlusgradeRequest,
} from '@models/requests/plusgrade-request.model';
import { ApiResponse } from '@models/responses/api-response.model';
import {
  PlusgradeResponse,
  PlusgradeResponseStatus,
  PlusgradeUrlType,
} from '@models/responses/plusgrade-response.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PlusgradeService } from '@services/api-clients/plusgrade.service';
import { BrowserService } from '@services/browser/browser.service';
import { ModalService } from '@services/modal/modal.service';
import { mergeMap, map, first, of } from 'rxjs';

@Injectable()
export class PlusgradeEffect {
  constructor(
    private actions$: Actions,
    private plusgradeService: PlusgradeService,
    private modalService: ModalService,
    private browserService: BrowserService,
  ) {}

  getActiveStationsWithMarkets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingTripDetailsInit),
      mergeMap((action) => {
        const request = {
          lastName: action.lastName,
          pnr: action.confirmationNumber,
          origin: PlusgradeCallOrigin.TRIPS,
        } as PlusgradeRequest;

        if (action.isAPG) {
          return of(appNoOp());
        }

        return this.plusgradeService.getEligibility(request).pipe(
          map((response) =>
            plusgradeGetEligibilitySuccess({
              payload: response.data,
            }),
          ),
        );
      }),
    );
  });

  getPlusgradeEligibility$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(plusgradeGetEligibility),
      mergeMap((action) => {
        return this.plusgradeService.getEligibility(action.request).pipe(
          map((response: ApiResponse<PlusgradeResponse>) => {
            if (response.data?.responseStatus) {
              switch (response.data.responseStatus) {
                case PlusgradeResponseStatus.ELIGIBLE:
                  /* eslint-disable no-case-declarations */
                  // show bid modal.
                  const modalCopy =
                    response.data.urlType === PlusgradeUrlType.MODIFY
                      ? action.modals.manage?.modalContent
                      : action.modals.offer?.modalContent;
                  const bsModalRef = this.modalService.show(
                    ConfirmationMessageModalComponent,
                    {
                      class: MODAL_DIALOG_CENTER,
                      initialState: {
                        confirmBtnText: modalCopy?.confirmText,
                        declineBtnText: modalCopy?.declineText,
                        messageText: modalCopy?.messageText,
                        titleText: modalCopy?.titleText,
                      },
                    },
                  );

                  bsModalRef.content?.response
                    .pipe(first())
                    .subscribe((value) => {
                      if (value) {
                        this.browserService.open({
                          url: response.data.url,
                        });
                      }
                    });
                  /* eslint-enable no-case-declarations */
                  break;
                case PlusgradeResponseStatus.NOT_ELIGIBLE:
                  // not eligible
                  this.modalService.show(ConfirmationMessageModalComponent, {
                    class: MODAL_DIALOG_CENTER,
                    initialState: {
                      confirmBtnText:
                        action.modals.notEligible?.modalContent?.confirmText,
                      messageText:
                        action.modals.notEligible?.modalContent?.messageText,
                    },
                  });
                  break;
                case PlusgradeResponseStatus.PNR_NOT_FOUND:
                  // not found
                  this.modalService.show(ConfirmationMessageModalComponent, {
                    class: MODAL_DIALOG_CENTER,
                    initialState: {
                      confirmBtnText:
                        action.modals.notFound?.modalContent?.confirmText,
                      messageText:
                        action.modals.notFound?.modalContent?.messageText,
                    },
                  });
                  break;
              }
            } else {
              // not found
              this.modalService.show(ConfirmationMessageModalComponent, {
                class: MODAL_DIALOG_CENTER,
                initialState: {
                  confirmBtnText:
                    action.modals.notFound?.modalContent?.confirmText,
                  messageText:
                    action.modals.notFound?.modalContent?.messageText,
                },
              });
            }

            return appNoOp();
          }),
        );
      }),
    );
  });
}
